import { Injectable } from "@angular/core";
import { SupabaseService } from "./supabase.service";
import { AuthService } from "./auth.service";
import {
  PortfolioProject,
  ProjectMedia,
  CreateProjectRequest,
  UploadMediaRequest,
  MediaUploadResponse,
  ProjectWithMedia,
  TalentProfile,
  WorkStatus,
} from "../models/portfolio.interface";
import { Observable, from, throwError } from "rxjs";
import { map, switchMap, catchError } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class PortfolioService {
  constructor(
    private supabaseService: SupabaseService,
    private authService: AuthService
  ) {}

  /** Cover = first image by order_index, else first media. */
  private static getCoverMedia(media: ProjectMedia[]): ProjectMedia | undefined {
    if (!media?.length) return undefined;
    const byOrder = [...media].sort((a, b) => a.order_index - b.order_index);
    return byOrder.find((m) => m.media_type === "image") ?? byOrder[0];
  }

  /** Sort media by order_index so thumbnails match upload order. */
  private static sortMediaByOrder(media: ProjectMedia[]): ProjectMedia[] {
    if (!media?.length) return media;
    return [...media].sort((a, b) => a.order_index - b.order_index);
  }

  /**
   * Get current user's portfolio projects with media
   */
  getUserProjects(): Observable<ProjectWithMedia[]> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .select(
          `
          *,
          media:project_media(*)
        `
        )
        .eq("user_id", userId)
        .order("order_index", { ascending: true })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;

        return (data || []).map((project) => {
          const media = PortfolioService.sortMediaByOrder(project.media || []);
          return {
            ...project,
            media,
            cover_media: PortfolioService.getCoverMedia(media),
          };
        });
      }),
      catchError((error) => {
        console.error("Error fetching user projects:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get projects for a specific user (for viewing talent profiles)
   */
  getUserProjectsById(userId: string): Observable<ProjectWithMedia[]> {
    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .select(
          `
          *,
          media:project_media(*)
        `
        )
        .eq("user_id", userId)
        .order("order_index", { ascending: true })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;

        return (data || []).map((project) => {
          const media = PortfolioService.sortMediaByOrder(project.media || []);
          return {
            ...project,
            media,
            cover_media: PortfolioService.getCoverMedia(media),
          };
        });
      }),
      catchError((error) => {
        console.error("Error fetching projects for user:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new portfolio project
   */
  createProject(
    projectData: CreateProjectRequest
  ): Observable<PortfolioProject> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    // Get the next order index
    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .select("order_index")
        .eq("user_id", userId)
        .order("order_index", { ascending: false })
        .limit(1)
    ).pipe(
      switchMap(({ data: projects, error: countError }) => {
        if (countError) throw countError;

        const nextOrder =
          projects && projects.length > 0 ? projects[0].order_index + 1 : 0;

        return from(
          this.supabaseService
            .getClient()
            .from("portfolio_projects")
            .insert([
              {
                ...projectData,
                user_id: userId,
                order_index: nextOrder,
              },
            ])
            .select()
            .single()
        );
      }),
      map(({ data, error }) => {
        if (error) throw error;
        return data;
      }),
      catchError((error) => {
        console.error("Error creating project:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update a portfolio project
   */
  updateProject(
    projectId: string,
    updates: Partial<CreateProjectRequest>
  ): Observable<PortfolioProject> {
    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .update(updates)
        .eq("id", projectId)
        .select()
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data;
      }),
      catchError((error) => {
        console.error("Error updating project:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a portfolio project and its media
   */
  deleteProject(projectId: string): Observable<void> {
    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .delete()
        .eq("id", projectId)
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error deleting project:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Compress image file (resize and reduce quality) for smaller uploads.
   * Returns original file if not an image or compression fails.
   */
  private async compressImage(file: File): Promise<File> {
    const maxWidth = 1920;
    const maxHeight = 1920;
    const quality = 0.85;
    const imageTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!imageTypes.includes(file.type)) {
      return file;
    }

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement("canvas");
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const ext = file.name.split(".").pop() ?? "jpg";
            const name = file.name.replace(/\.[^.]+$/, `.${ext}`);
            resolve(new File([blob], name, { type: blob.type }));
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  }

  /**
   * Upload and compress media file
   */
  async uploadMedia(
    file: File,
    projectId: string,
    mediaType: "image" | "video",
    iscover = false
  ): Promise<MediaUploadResponse> {
    try {
      let fileToUpload = file;
      if (mediaType === "image") {
        fileToUpload = await this.compressImage(file);
      }

      const fileExt = fileToUpload.name.split(".").pop();
      const fileName = `${projectId}/${Date.now()}.${fileExt}`;
      // Path is inside bucket; bucket is already "project-media"
      const filePath = fileName;

      const { data: uploadData, error: uploadError } =
        await this.supabaseService
          .getClient()
          .storage.from("project-media")
          .upload(filePath, fileToUpload);

      if (uploadError) {
        throw uploadError;
      }

      const {
        data: { publicUrl },
      } = this.supabaseService
        .getClient()
        .storage.from("project-media")
        .getPublicUrl(filePath);

      // Get next order index and whether project already has an image
      const { data: existingMedia } = await this.supabaseService
        .getClient()
        .from("project_media")
        .select("order_index, media_type")
        .eq("project_id", projectId)
        .order("order_index", { ascending: false });

      const nextOrder =
        existingMedia && existingMedia.length > 0
          ? existingMedia[0].order_index + 1
          : 0;

      const hasExistingImage =
        existingMedia?.some((m) => m.media_type === "image") ?? false;
      const isFirstImage = mediaType === "image" && !hasExistingImage;
      const setAsCover = iscover || isFirstImage;

      if (setAsCover) {
        await this.supabaseService
          .getClient()
          .from("project_media")
          .update({ is_cover_media: false })
          .eq("project_id", projectId);
      }

      // Create media record (cover = first image uploaded)
      const { data: mediaData, error: mediaError } = await this.supabaseService
        .getClient()
        .from("project_media")
        .insert([
          {
            project_id: projectId,
            media_url: publicUrl,
            media_type: mediaType,
            order_index: nextOrder,
            is_cover_media: setAsCover,
            file_size: fileToUpload.size,
          },
        ])
        .select()
        .single();

      if (mediaError) {
        throw mediaError;
      }

      return {
        success: true,
        media_id: mediaData.id,
        media_url: publicUrl,
        file_size: file.size,
      };
    } catch (error) {
      console.error("Error uploading media:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Upload failed",
      };
    }
  }

  /**
   * Delete media file
   */
  deleteMedia(mediaId: string): Observable<void> {
    return from(
      this.supabaseService
        .getClient()
        .from("project_media")
        .delete()
        .eq("id", mediaId)
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error deleting media:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update project order
   */
  updateProjectOrder(projectId: string, newOrder: number): Observable<void> {
    return from(
      this.supabaseService
        .getClient()
        .from("portfolio_projects")
        .update({ order_index: newOrder })
        .eq("id", projectId)
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error updating project order:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update media order
   */
  updateMediaOrder(mediaId: string, newOrder: number): Observable<void> {
    return from(
      this.supabaseService
        .getClient()
        .from("project_media")
        .update({ order_index: newOrder })
        .eq("id", mediaId)
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error updating media order:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Set cover media for a project
   */
  setCoverMedia(projectId: string, mediaId: string): Observable<void> {
    return from(
      // First, unset any existing cover media for this project
      this.supabaseService
        .getClient()
        .from("project_media")
        .update({ is_cover_media: false })
        .eq("project_id", projectId)
    ).pipe(
      switchMap(() =>
        // Then set the new cover media
        from(
          this.supabaseService
            .getClient()
            .from("project_media")
            .update({ is_cover_media: true })
            .eq("id", mediaId)
        )
      ),
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error setting cover media:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update user's work status
   */
  updateWorkStatus(workStatus: WorkStatus): Observable<void> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .update({ work_status: workStatus })
        .eq("id", userId)
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error updating work status:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if a username is available (not taken by another user).
   * Pass excludeUserId to allow the current user to keep their own username.
   */
  checkUsernameAvailability(
    username: string,
    excludeUserId?: string
  ): Observable<boolean> {
    const normalized = username.trim().toLowerCase();
    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .select("id")
        .eq("username", normalized)
        .limit(1)
        .maybeSingle()
    ).pipe(
      map(({ data }) => {
        if (!data) return true;
        if (excludeUserId && data.id === excludeUserId) return true;
        return false;
      }),
      catchError(() => throwError(() => new Error("Check failed")))
    );
  }

  /**
   * Fetch only profile_theme for a public profile by username.
   * Use to apply theme before loading full profile (e.g. for skeleton loading).
   * Returns null if not found or profile is private.
   */
  getProfileThemeByUsername(
    username: string
  ): Observable<"light" | "dark" | null> {
    const normalized = username.trim().toLowerCase();
    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .select("profile_theme")
        .eq("username", normalized)
        .eq("profile_public", true)
        .maybeSingle()
    ).pipe(
      map(({ data }) =>
        data?.profile_theme === "dark" || data?.profile_theme === "light"
          ? data.profile_theme
          : null
      ),
      catchError(() => throwError(() => new Error("Failed to load theme")))
    );
  }

  /**
   * Get public profile by username (only when profile_public is true).
   * Returns null if not found or profile is private.
   */
  getProfileByUsername(username: string): Observable<TalentProfile | null> {
    const normalized = username.trim().toLowerCase();
    const publicColumns =
      "id, first_name, last_name, display_name, location, job_title, " +
      "experience_years, skills, education, work_history, certifications, " +
      "profile_summary, preferences, work_status, profile_ranking, " +
      "profile_completed, website, profile_picture_url, profile_links, " +
      "profile_section_order, username, profile_public, profile_theme";
    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .select(
          `${publicColumns}, portfolio_projects(*, media:project_media(*))`
        )
        .eq("username", normalized)
        .eq("profile_public", true)
        .maybeSingle()
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) return null;
        const projects = (data.portfolio_projects || []).map((project: any) => {
          const media = PortfolioService.sortMediaByOrder(
            project.media || []
          );
          return {
            ...project,
            media,
            cover_media: PortfolioService.getCoverMedia(media),
          };
        });
        return {
          ...data,
          portfolio_projects: projects,
        } as TalentProfile;
      }),
      catchError(() => throwError(() => new Error("Failed to load profile")))
    );
  }

  /**
   * Get user's talent profile with projects
   */
  getTalentProfile(userId?: string): Observable<TalentProfile> {
    const targetUserId = userId || this.authService.getCurrentUser()?.id;
    if (!targetUserId) {
      return throwError(() => new Error("User ID required"));
    }

    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .select(
          `
          *,
          portfolio_projects(
            *,
            media:project_media(*)
          )
        `
        )
        .eq("id", targetUserId)
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;

        // Process portfolio projects to include cover media
        const projects = (data.portfolio_projects || []).map(
          (project: any) => {
            const media = PortfolioService.sortMediaByOrder(
              project.media || []
            );
            return {
              ...project,
              media,
              cover_media: PortfolioService.getCoverMedia(media),
            };
          }
        );

        return {
          ...data,
          portfolio_projects: projects,
        } as TalentProfile;
      }),
      catchError((error) => {
        console.error("Error fetching talent profile:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Columns on users table that can be updated via the API.
   * Omit keys not in this list to avoid PGRST204 (column not in schema).
   */
  private static readonly USERS_UPDATE_KEYS: ReadonlySet<string> = new Set([
    "first_name",
    "last_name",
    "display_name",
    "job_title",
    "location",
    "experience_years",
    "skills",
    "education",
    "work_history",
    "certifications",
    "profile_summary",
    "resume_text",
    "resume_url",
    "work_status",
    "profile_ranking",
    "profile_completed",
    "linkedin_resume_url",
    "website",
    "tagline",
    "resume_original_filename",
    "profile_picture_url",
    "preferences",
    "profile_links",
    "profile_section_order",
    "username",
    "profile_public",
    "profile_theme",
  ]);

  /**
   * Update user's talent profile
   */
  updateTalentProfile(updates: Partial<TalentProfile>): Observable<TalentProfile> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    const { portfolio_projects, subscription, ...rest } = updates;
    const userUpdates: Record<string, unknown> = {};
    for (const key of Object.keys(rest)) {
      if (PortfolioService.USERS_UPDATE_KEYS.has(key)) {
        const v = (rest as Record<string, unknown>)[key];
        userUpdates[key] = v;
      }
    }

    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .update(userUpdates)
        .eq("id", userId)
        .select()
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data as TalentProfile;
      }),
      catchError((error) => {
        console.error("Error updating talent profile:", error);
        return throwError(() => error);
      })
    );
  }
}
